import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSystemTables } from '../../hooks/UseSystemTables';
import Order from '../../models/Order';
import ShipmentsPopup from '../ShipmentsPopup/ShipmentsPopup';

import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';

interface OrderCardProps {
  order: Order;
}

const OrderCard: React.FC<OrderCardProps> = ({ order }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isShipmentsOpen, setIsShipmentsOpen] = useState(false);
  const [currentStatus] = useState(order.status);
  const theme = useTheme();
  
  // Fetch system tables
  const { data: systemTablesData } = useSystemTables();

  const client = systemTablesData?.data?.clients?.find(
    (client) => client.clientInnerId === order.clientInnerId
  );
  const clientName = client ? client.clientName : 'Client not found';

  const status = systemTablesData?.data?.orderStatus?.find(
    (status) => status._id === currentStatus
  );
  const statusNameDisplay = i18n.language === 'he' ? status?.name_hebrew : status?.name;

  const location = systemTablesData?.data?.warehouseLocations?.find(
    (location) => location._id == order.origin
  );
  const locationName = i18n.language === 'he' ? location?.nameHeb : location?.nameEng;

  const trasnsportationCompany = systemTablesData?.data?.transportCompanies?.find(
    (transportCompany) => transportCompany.companyInnerId == order.transportationCompany
  );
  const trasnsportationCompanyName = trasnsportationCompany ? trasnsportationCompany.companyName : 'N/A';

  const statusKeyMapping: { [key: number]: keyof typeof theme.palette.orderStatus } = {
    1: 'pending',
    2: 'approved',
    3: 'processing',
    4: 'shipped',
    5: 'delivered',
    6: 'canceled',
    7: 'onHold',
    8: 'rejected',
    9: 'awaitingPickup',
  };
  
  const statusKey = statusKeyMapping[order.status];
  const statusColors = theme.palette.orderStatus[statusKey];

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleShipmentsClick = () => {
    setIsShipmentsOpen(true);
  };

  const handleShipmentsClose = () => {
    setIsShipmentsOpen(false);
  };

  const handleDuplicateOrder = () => {
    // Prepare the order data for the new order form, excluding dates
    const orderData = {
      itemNum: order.itemNum,
      origin: order.origin,
      amount: order.amount.toString(),
      amountRestriction: !!order.tonAmount, // If tonAmount exists, set amountRestriction to true
      isSelfShipping: order.isSelfShipping,
      clientInnerId: order.clientInnerId,
      tonAmount: order.tonAmount?.toString() || '',
      transportationCompany: order.transportationCompany,
      comment: order.comment || '',
      // Exclude requestedTime to let user set a new date
    };

    navigate('/new-order', { state: { preFilledData: orderData } });
  };

  const handleEditOrder = () => {
    // Helper function to convert Date to datetime-local format (YYYY-MM-DDTHH:mm)
    const formatDateForInput = (date: Date | undefined): string => {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Prepare the order data for editing, including requestedTime
    const orderData = {
      itemNum: order.itemNum,
      origin: order.origin,
      amount: order.amount.toString(),
      amountRestriction: !!order.tonAmount, // If tonAmount exists, set amountRestriction to true
      isSelfShipping: order.isSelfShipping,
      clientInnerId: order.clientInnerId,
      tonAmount: order.tonAmount?.toString() || '',
      transportationCompany: order.transportationCompany,
      comment: order.comment || '',
      requestedTime: formatDateForInput(order.requestedTime),
    };

    navigate('/new-order', { state: { preFilledData: orderData, isEdit: true, orderId: order.orderId } });
  };

  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'short',
      timeStyle: 'short',
      hourCycle: 'h23',
    }).format(date);
  };

  return (
    <Card sx={{ marginBottom: 2 }}>
      {/* Custom Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" p={2}>
        {/* Left Section: Order ID and Client Name */}
        <Box>
          <Typography variant="h6">{order.orderId}</Typography>
          <Typography variant="subtitle2">{clientName}</Typography>
        </Box>
        {/* Right Section: Requested Time, Amount, Status Chip and Expand Button in a row */}
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="body2">
            {t('Order.RequestedTime')}: {order.requestedTime ? formatDateTime(order.requestedTime) : 'N/A'}
          </Typography>
          <Typography variant="body2">
            {t('Order.Amount')}: {order.amount}
          </Typography>
          <Chip
            label={statusNameDisplay}
            sx={{
              backgroundColor: statusColors.main,
              color: statusColors.contrastText,
              fontWeight: 'bold',
              borderRadius: '12px',
              padding: '0.3rem 0.8rem',
              fontSize: '0.9rem',
            }}
          />
          <IconButton
            size="small"
            onClick={toggleExpand}
            aria-label="expand"
            sx={{ color: 'text.primary', p: 0, minWidth: 0, width: 24, height: 24 }}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      {/* Collapsible Content */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ backgroundColor: 'white' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                {t('OrderCard.Titles.ShippingDetails')}
              </Typography>
              <Typography variant="body2">
                {t('Order.TransportationCompany')}: {trasnsportationCompanyName}
              </Typography>
              <Typography variant="body2">
                {t('Order.ShippingCertificateNumber')}: {order.shippingCertificateNumber}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                {t('OrderCard.Titles.TimeDetails')}
              </Typography>
              <Typography variant="body2">
                {t('Order.ExpectedReceiveTime')}: {order.expectedReceiveTime ? formatDateTime(order.expectedReceiveTime) : 'N/A'}
              </Typography>
              <Typography variant="body2">
                {t('Order.OrderCreationTime')}: {order.orderCreationTime ? formatDateTime(order.orderCreationTime) : 'N/A'}
              </Typography>
              <Typography variant="body2">
                {t('Order.RequestedTime')}: {order.requestedTime ? formatDateTime(order.requestedTime) : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                {t('OrderCard.Titles.OrderDetails')}
              </Typography>
              <Typography variant="body2">
                {t('Order.CustomerNum')}: {order.clientInnerId}
              </Typography>
              <Typography variant="body2">
                {t('OrderCard.Labels.ClientName')}: {clientName}
              </Typography>
              <Typography variant="body2">
                {t('Order.ItemNum')}: {order.itemNum}
              </Typography>
              <Typography variant="body2">
                {t('Order.Origin')}: {locationName}
              </Typography>
              <Typography variant="body2">
                {t('Order.Amount')}: {order.amount}
              </Typography>
              {order.tonAmount && (
                <Typography variant="body2">
                  {t('Order.TonAmount')}: {order.tonAmount}
                </Typography>
              )}
              <Typography variant="body2">
                {t('Order.IsSelfShipping.Title')}: {order.isSelfShipping ? t('Order.IsSelfShipping.Yes') : t('Order.IsSelfShipping.No')}
              </Typography>
              {order.comment && (
                <Typography variant="body2">
                  {t('Order.Comment')}: {order.comment}
                </Typography>
              )}
            </Grid>
          </Grid>
          {/* Action Buttons */}
          <Box mt={2} display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            {/* Edit Order Button */}
            <Button
              variant="contained"
              startIcon={i18n.language === 'he' ? undefined : <EditIcon />}
              endIcon={i18n.language === 'he' ? <EditIcon /> : undefined}
              onClick={handleEditOrder}
              sx={{ 
                minWidth: 150,
                backgroundColor: 'var(--button-bg)',
                color: 'var(--button-text)',
                '&:hover': {
                  backgroundColor: '#cfdad1',
                },
                ...(i18n.language === 'he' && {
                  '& .MuiButton-endIcon': {
                    marginRight: '8px',
                  },
                }),
              }}
            >
              {t('OrderCard.EditOrder')}
            </Button>

            {/* Duplicate Order Button */}
            <Button
              variant="contained"
              startIcon={i18n.language === 'he' ? undefined : <ContentCopyIcon />}
              endIcon={i18n.language === 'he' ? <ContentCopyIcon /> : undefined}
              onClick={handleDuplicateOrder}
              sx={{ 
                minWidth: 150,
                backgroundColor: 'var(--button-bg)',
                color: 'var(--button-text)',
                '&:hover': {
                  backgroundColor: '#cfdad1',
                },
                ...(i18n.language === 'he' && {
                  '& .MuiButton-endIcon': {
                    marginRight: '8px',
                  },
                }),
              }}
            >
              {t('OrderCard.DuplicateOrder')}
            </Button>
            
            {/* Shipments Button */}
            {order.shipments && order.shipments.length > 0 && (
              <Button
                variant="contained"
                startIcon={i18n.language === 'he' ? undefined : <LocalShippingIcon />}
                endIcon={i18n.language === 'he' ? <LocalShippingIcon /> : undefined}
                onClick={handleShipmentsClick}
                sx={{ 
                  minWidth: 200,
                  backgroundColor: 'var(--button-bg)',
                  color: 'var(--button-text)',
                  '&:hover': {
                    backgroundColor: '#cfdad1',
                  },
                  ...(i18n.language === 'he' && {
                    '& .MuiButton-endIcon': {
                      marginRight: '8px',
                    },
                  }),
                }}
              >
                {t('OrderCard.ViewShipments')} ({order.shipments.length})
              </Button>
            )}
          </Box>
        </CardContent>
      </Collapse>
      {/* Shipments Popup */}
      {order.shipments && order.shipments.length > 0 && (
        <ShipmentsPopup
          open={isShipmentsOpen}
          onClose={handleShipmentsClose}
          shipments={order.shipments}
          orderId={order.orderId}
        />
      )}
    </Card>
  );
};

export default OrderCard;